// filepath: src/firebase/firestore/useReportTasks.js
import {useCallback, useEffect, useRef, useState} from 'react';
import {getApiBase} from '../../config/apiBase';

function authHeaders() {
    const token = localStorage.getItem('access_token');
    return token ? {'Authorization': `Bearer ${token}`} : {};
}

const TASK_POLL_MS = 15_000;

/**
 * Fetch report tasks from dash-api /api/reports/{reportId}/tasks.
 * Returns { data, loading, error }.
 *
 * Pass options.enabled = false to suspend all fetching (e.g. when the panel is hidden).
 * When enabled flips back to true the hook fetches immediately and resumes polling.
 */
export function useReportTasks(reportId, {enabled = true} = {}) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const initialLoadDone = useRef(false);

    const loadTasks = useCallback((isBackground = false) => {
        if (!reportId) {
            setData([]);
            setLoading(false);
            setError(null);
            initialLoadDone.current = false;
            return;
        }

        // Only show loading spinner on the very first fetch, not on background polls
        if (!isBackground) {
            setLoading(true);
        }
        setError(null);

        fetch(`${getApiBase()}/reports/${encodeURIComponent(reportId)}/tasks`, {headers: authHeaders()})
            .then((r) => r.ok ? r.json() : Promise.reject(new Error(`${r.status}`)))
            .then((list) => {
                const filtered = Array.isArray(list) ? list.filter(t => t.status === 'suggested') : [];
                setData(filtered);
                setLoading(false);
                initialLoadDone.current = true;
            })
            .catch((err) => {
                setError(err);
                setLoading(false);
            });
    }, [reportId]);

    useEffect(() => {
        if (!enabled) return undefined;
        loadTasks();
    }, [loadTasks, enabled]);

    useEffect(() => {
        if (!enabled) return undefined;
        const onTasksChanged = (event) => {
            const changedReportId = event?.detail?.reportId;
            if (!changedReportId || changedReportId === reportId) {
                loadTasks(initialLoadDone.current);
            }
        };
        window.addEventListener('report-tasks-changed', onTasksChanged);
        return () => window.removeEventListener('report-tasks-changed', onTasksChanged);
    }, [reportId, loadTasks, enabled]);

    // Background analyzers write tasks asynchronously after version snapshots.
    useEffect(() => {
        if (!reportId || !enabled) return undefined;
        const timer = setInterval(() => loadTasks(true), TASK_POLL_MS);
        return () => clearInterval(timer);
    }, [reportId, loadTasks, enabled]);

    return {data, loading, error, refetch: loadTasks};
}
